import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckoutSEO } from '../components/SEO';
import { toast } from 'sonner';
import { User, LogIn, Package, Share2, Newspaper, FileText, Wallet, Star, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Support multiple item types: publication, social_page, print_media, bundle
  const { 
    itemType = 'publication', 
    publication, 
    socialPage, 
    printMedia, 
    bundle,
    package: selectedPackage 
  } = location.state || {};
  
  // Get the item and its details based on type
  const getItemDetails = () => {
    switch (itemType) {
      case 'social_page':
        return {
          item: socialPage,
          name: socialPage?.name,
          subtitle: `@${socialPage?.handle} • ${socialPage?.platform}`,
          icon: Share2,
          price: selectedPackage?.price || 0,
          packageName: selectedPackage?.name,
          returnPath: '/marketplace?type=social_media'
        };
      case 'print_media':
        return {
          item: printMedia,
          name: printMedia?.name,
          subtitle: `${printMedia?.media_type} • ${printMedia?.language || 'English'}`,
          icon: Newspaper,
          price: selectedPackage?.price || 0,
          packageName: selectedPackage?.name,
          returnPath: '/marketplace?type=print_media'
        };
      case 'bundle':
        return {
          item: bundle,
          name: bundle?.name,
          subtitle: bundle?.bundle_type?.replace(/_/g, ' '),
          icon: Package,
          price: bundle?.bundle_price || 0,
          packageName: bundle?.short_description || 'Bundle Package',
          returnPath: '/marketplace?type=bundles'
        };
      default: // publication
        return {
          item: publication,
          name: publication?.name,
          subtitle: `DA ${publication?.da || 'N/A'} • ${publication?.category || 'General'}`,
          icon: FileText,
          price: selectedPackage?.price || 0,
          packageName: selectedPackage?.name,
          returnPath: '/marketplace'
        };
    }
  };
  
  const itemDetails = getItemDetails();
  
  // GST rates: 18% for most services, 5% for print media
  const getGSTRate = () => {
    return itemType === 'print_media' ? 5 : 18;
  };
  
  const gstRate = getGSTRate();
  
  const [writingService, setWritingService] = useState('');
  const [extraLinks, setExtraLinks] = useState(0);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    gstin: '',
    address: ''
  });
  const [processing, setProcessing] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Partner credits state
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [usePartnerCredits, setUsePartnerCredits] = useState(false);
  
  // Check if we have valid item data
  const hasValidItem = itemDetails.item !== undefined && itemDetails.item !== null;
  const hasValidPackage = selectedPackage !== undefined || itemType === 'bundle';

  useEffect(() => {
    if (!hasValidItem || !hasValidPackage) {
      toast.error('No package selected');
      navigate(itemDetails.returnPath || '/marketplace');
      return;
    }
    
    checkAuthAndLoadUser();
  }, []);

  // Check partner discount when total changes
  useEffect(() => {
    if (isLoggedIn) {
      checkPartnerDiscount();
    }
  }, [isLoggedIn, writingService, extraLinks]);

  const checkPartnerDiscount = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Send total with GST for partner discount calculation
      const totalWithGST = calculateTotalWithGST();
      const response = await axios.post(
        `${API}/orders/check-partner-discount?amount=${totalWithGST}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPartnerInfo(response.data);
      
      // Auto-select credits if partner has enough
      if (response.data.is_partner && response.data.can_use_credits) {
        setUsePartnerCredits(true);
      }
    } catch (error) {
      // Not a partner or error - ignore silently
      setPartnerInfo(null);
    }
  };

  const checkAuthAndLoadUser = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }

    try {
      // Verify token and get fresh user data
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = response.data;
      setIsLoggedIn(true);
      
      // Pre-fill customer data from user profile
      setCustomerData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        company: userData.company || '',
        gstin: userData.gstin || '',
        address: userData.address || ''
      });
    } catch (error) {
      console.error('Auth check failed:', error);
      // Token invalid, clear and show login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    // Store the checkout state to return after login
    sessionStorage.setItem('checkoutReturn', JSON.stringify(location.state));
    navigate('/login', { state: { returnTo: '/checkout', checkoutData: location.state } });
  };

  const calculateTotal = () => {
    let total = itemDetails.price || 0;
    
    // Add-ons only apply to publication type (add-ons are also base prices)
    if (itemType === 'publication') {
      if (writingService === 'standard') total += 2500;
      if (writingService === 'premium') total += 4000;
      if (writingService === 'interview') total += 6000;
      total += extraLinks * 1000;
    }
    
    return total;
  };

  // Calculate GST amount
  const calculateGST = () => {
    const baseTotal = calculateTotal();
    return Math.round(baseTotal * (gstRate / 100));
  };

  // Calculate total with GST
  const calculateTotalWithGST = () => {
    return calculateTotal() + calculateGST();
  };

  const getDiscountedTotal = () => {
    if (usePartnerCredits && partnerInfo?.is_partner) {
      // Partner discount already applies to total including GST
      return partnerInfo.discounted_price || calculateTotalWithGST();
    }
    return calculateTotalWithGST();
  };

  // Get the base discounted price (before GST) for display purposes
  const getBaseDiscountedPrice = () => {
    if (usePartnerCredits && partnerInfo?.is_partner) {
      // Calculate base from discounted total
      const discountedTotal = partnerInfo.discounted_price || calculateTotalWithGST();
      return Math.round(discountedTotal / (1 + gstRate / 100));
    }
    return calculateTotal();
  };

  const handleCheckout = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to continue');
      handleLogin();
      return;
    }

    if (!customerData.name || !customerData.email || !customerData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    const token = localStorage.getItem('token');

    try {
      setProcessing(true);
      
      // Build order data based on item type
      let orderData = {
        item_type: itemType,
        customer_name: customerData.name,
        customer_email: customerData.email,
        customer_phone: customerData.phone,
        customer_company: customerData.company || undefined,
        customer_gstin: customerData.gstin || undefined,
        customer_address: customerData.address || undefined,
        use_partner_credits: usePartnerCredits && partnerInfo?.can_use_credits
      };

      switch (itemType) {
        case 'social_page':
          orderData.social_page_id = socialPage.id;
          orderData.package_id = selectedPackage.id;
          break;
        case 'print_media':
          orderData.print_media_id = printMedia.id;
          orderData.package_id = selectedPackage.id;
          break;
        case 'bundle':
          orderData.bundle_id = bundle.id;
          break;
        default: // publication
          orderData.publication_id = publication.id;
          orderData.package_id = selectedPackage.id;
          orderData.add_ons = {
            writing_service: writingService || undefined,
            extra_links: extraLinks > 0 ? extraLinks : undefined
          };
          break;
      }

      const response = await axios.post(
        `${API}/orders/create`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If paid with credits, redirect to success
      if (response.data.paid_with_credits) {
        toast.success(response.data.message);
        navigate('/my-orders');
        return;
      }

      const { order_id, razorpay_order_id, amount, razorpay_key } = response.data;
      
      await handlePayment(order_id, razorpay_order_id, amount, razorpay_key);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (orderId, razorpayOrderId, amount, razorpayKey) => {
    const options = {
      key: razorpayKey,
      amount: amount * 100,
      currency: 'INR',
      name: 'BusinessMint PR',
      description: `Order for ${itemDetails.name}`,
      order_id: razorpayOrderId,
      prefill: {
        name: customerData.name,
        email: customerData.email,
        contact: customerData.phone
      },
      theme: {
        color: '#D4AF37'
      },
      handler: async function (response) {
        try {
          setProcessing(true);
          
          await axios.post(
            `${API}/orders/${orderId}/payment/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }
          );
          
          toast.success('Payment successful!');
          navigate(`/order/${orderId}/submit`);
        } catch (error) {
          console.error('Payment verification error:', error);
          toast.error(error.response?.data?.detail || 'Payment verification failed');
          setProcessing(false);
        }
      },
      modal: {
        ondismiss: function() {
          toast.error('Payment cancelled');
          setProcessing(false);
        }
      }
    };

    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.on('payment.failed', function (response) {
      console.error('Payment failed:', response.error);
      toast.error(`Payment failed: ${response.error.description}`);
      setProcessing(false);
    });
    
    razorpayInstance.open();
  };

  if (!hasValidItem || !hasValidPackage) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
          <p className="font-manrope text-[#737373]">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <CheckoutSEO />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-12 text-[#1a1a1a]">
          Checkout
        </h1>
        
        {/* Login Required Banner */}
        {!isLoggedIn && (
          <div className="mb-8 border border-[#D4AF37] p-6 bg-[#fafafa]">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] text-[#1a1a1a] flex items-center justify-center">
                  <LogIn className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-bold text-[#1a1a1a]">Login Required</h3>
                  <p className="font-manrope text-sm text-[#737373]">Please login or create an account to complete your purchase</p>
                </div>
              </div>
              <button
                onClick={handleLogin}
                data-testid="login-to-checkout-btn"
                className="px-8 py-3 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300"
              >
                Login / Register
              </button>
            </div>
          </div>
        )}

        {/* Logged In User Info */}
        {isLoggedIn && (
          <div className="mb-8 border border-[#1a3d1a] bg-[#0d1f0d] p-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-[#4ade80]" />
              <span className="font-manrope text-sm text-[#4ade80]">
                Logged in as <strong>{customerData.name}</strong> ({customerData.email})
              </span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="border border-[#e5e5e5] bg-[#fafafa] p-6">
              <h2 className="font-playfair text-2xl font-bold mb-4 text-[#1a1a1a]">Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-manrope text-sm text-[#737373]">
                    {itemType === 'bundle' ? 'Bundle' : itemType === 'social_page' ? 'Social Page' : itemType === 'print_media' ? 'Print Media' : 'Publication'}
                  </span>
                  <span className="font-manrope text-sm font-semibold text-[#1a1a1a]">{itemDetails.name}</span>
                </div>
                {itemDetails.subtitle && (
                  <div className="flex justify-between">
                    <span className="font-manrope text-sm text-[#737373]">Details</span>
                    <span className="font-manrope text-sm text-[#737373] capitalize">{itemDetails.subtitle}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-manrope text-sm text-[#737373]">
                    {itemType === 'bundle' ? 'Bundle' : 'Package'}
                  </span>
                  <span className="font-manrope text-sm font-semibold text-[#1a1a1a]">{itemDetails.packageName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-manrope text-sm text-[#737373]">Price</span>
                  <span className="font-mono text-sm font-semibold text-[#D4AF37]">₹{itemDetails.price?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
            
            {/* Add-ons - Only show for publication type */}
            {itemType === 'publication' && (
            <div className="border border-[#e5e5e5] bg-[#fafafa] p-6">
              <h2 className="font-playfair text-2xl font-bold mb-6 text-[#1a1a1a]">Add-ons</h2>
              
              <div className="mb-6">
                <label className="font-manrope text-sm font-semibold mb-3 block text-[#1a1a1a]">Writing Service</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="writing"
                      value=""
                      checked={writingService === ''}
                      onChange={(e) => setWritingService(e.target.value)}
                      data-testid="writing-service-none"
                      className="w-4 h-4 accent-[#D4AF37]"
                    />
                    <span className="font-manrope text-sm text-[#737373]">No writing service (I&apos;ll provide my own article)</span>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer border border-[#e5e5e5] bg-[#f5f5f5] p-3 hover:border-[#D4AF37] transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="writing"
                        value="standard"
                        checked={writingService === 'standard'}
                        onChange={(e) => setWritingService(e.target.value)}
                        data-testid="writing-service-standard"
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="font-manrope text-sm text-[#737373]">Standard (600-800 words)</span>
                    </div>
                    <span className="font-mono text-sm font-semibold text-[#D4AF37]">₹2,500</span>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer border border-[#e5e5e5] bg-[#f5f5f5] p-3 hover:border-[#D4AF37] transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="writing"
                        value="premium"
                        checked={writingService === 'premium'}
                        onChange={(e) => setWritingService(e.target.value)}
                        data-testid="writing-service-premium"
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="font-manrope text-sm text-[#737373]">Premium (Editorial tone)</span>
                    </div>
                    <span className="font-mono text-sm font-semibold text-[#D4AF37]">₹4,000</span>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer border border-[#e5e5e5] bg-[#f5f5f5] p-3 hover:border-[#D4AF37] transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="writing"
                        value="interview"
                        checked={writingService === 'interview'}
                        onChange={(e) => setWritingService(e.target.value)}
                        data-testid="writing-service-interview"
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="font-manrope text-sm text-[#737373]">Interview Style</span>
                    </div>
                    <span className="font-mono text-sm font-semibold text-[#D4AF37]">₹6,000</span>
                  </label>
                </div>
                {writingService && (
                  <div className="mt-4 p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded">
                    <p className="font-manrope text-xs text-[#525252]">
                      <span className="font-semibold text-[#D4AF37]">How it works:</span> After payment, you&apos;ll fill out a brief with your brand story, key points, and tone preference. Our writers will craft your article accordingly.
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="font-manrope text-sm font-semibold mb-3 block text-[#1a1a1a]">Extra Links (₹1,000 each)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={extraLinks}
                  onChange={(e) => setExtraLinks(Math.max(0, parseInt(e.target.value) || 0))}
                  data-testid="extra-links-input"
                  className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
            )}
            
            <div className={`border border-[#e5e5e5] bg-[#fafafa] p-6 ${!isLoggedIn ? 'opacity-50 pointer-events-none' : ''}`}>
              <h2 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">Billing Details</h2>
              <p className="font-manrope text-xs text-[#a3a3a3] mb-6">Pre-filled from your account. Update if needed for this order.</p>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Name *</label>
                    <input
                      type="text"
                      value={customerData.name}
                      onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                      data-testid="customer-name-input"
                      disabled={!isLoggedIn}
                      className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3]"
                    />
                  </div>
                  
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Phone *</label>
                    <input
                      type="tel"
                      value={customerData.phone}
                      onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                      data-testid="customer-phone-input"
                      disabled={!isLoggedIn}
                      className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Email *</label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                    data-testid="customer-email-input"
                    disabled={!isLoggedIn}
                    className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3]"
                  />
                </div>
                
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Company</label>
                  <input
                    type="text"
                    value={customerData.company}
                    onChange={(e) => setCustomerData({...customerData, company: e.target.value})}
                    data-testid="customer-company-input"
                    disabled={!isLoggedIn}
                    className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3]"
                  />
                </div>
                
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">GSTIN (for tax invoice)</label>
                  <input
                    type="text"
                    value={customerData.gstin}
                    onChange={(e) => setCustomerData({...customerData, gstin: e.target.value})}
                    data-testid="customer-gstin-input"
                    disabled={!isLoggedIn}
                    placeholder="22AAAAA0000A1Z5"
                    className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3] placeholder-[#a3a3a3]"
                  />
                </div>
                
                <div>
                  <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Billing Address</label>
                  <textarea
                    value={customerData.address}
                    onChange={(e) => setCustomerData({...customerData, address: e.target.value})}
                    data-testid="customer-address-input"
                    disabled={!isLoggedIn}
                    rows="2"
                    className="w-full border border-[#e5e5e5] bg-[#f5f5f5] text-[#1a1a1a] px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none disabled:bg-[#fafafa] disabled:text-[#a3a3a3]"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <div className="border border-[#e5e5e5] bg-[#fafafa] p-6 sticky top-24">
              <h2 className="font-playfair text-2xl font-bold mb-6 text-[#1a1a1a]">Payment Summary</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="font-manrope text-sm text-[#737373]">
                    {itemType === 'bundle' ? 'Bundle' : 'Package'} (Base Price)
                  </span>
                  <span className="font-mono text-sm text-[#1a1a1a]">₹{itemDetails.price?.toLocaleString('en-IN')}</span>
                </div>
                
                {itemType === 'publication' && writingService && (
                  <div className="flex justify-between">
                    <span className="font-manrope text-sm text-[#737373]">Writing Service</span>
                    <span className="font-mono text-sm text-[#1a1a1a]">
                      ₹{writingService === 'standard' ? '2,500' : writingService === 'premium' ? '4,000' : '6,000'}
                    </span>
                  </div>
                )}
                
                {itemType === 'publication' && extraLinks > 0 && (
                  <div className="flex justify-between">
                    <span className="font-manrope text-sm text-[#737373]">Extra Links ({extraLinks})</span>
                    <span className="font-mono text-sm text-[#1a1a1a]">₹{(extraLinks * 1000).toLocaleString('en-IN')}</span>
                  </div>
                )}
                
                {/* Partner Discount Section */}
                {partnerInfo?.is_partner && (
                  <>
                    <hr className="border-[#e5e5e5]" />
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Star className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-purple-800">Partner Discount</span>
                        <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full capitalize">
                          {partnerInfo.tier} Tier
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-purple-700">
                          {partnerInfo.discount_rate}% off (Save ₹{partnerInfo.discount_amount?.toLocaleString('en-IN')})
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setUsePartnerCredits(!usePartnerCredits)}
                          data-testid="use-credits-toggle"
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            usePartnerCredits ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            usePartnerCredits ? 'left-7' : 'left-1'
                          }`} />
                        </button>
                        <span className="text-sm text-purple-800">
                          Use Partner Credits
                        </span>
                      </div>
                      
                      {usePartnerCredits && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700">Credit Balance:</span>
                            <span className="font-mono font-semibold text-purple-800">
                              ₹{partnerInfo.credit_balance?.toLocaleString('en-IN')}
                            </span>
                          </div>
                          {!partnerInfo.can_use_credits && (
                            <p className="text-xs text-red-600 mt-2">
                              Insufficient credits. You need ₹{getDiscountedTotal().toLocaleString('en-IN')} but have ₹{partnerInfo.credit_balance?.toLocaleString('en-IN')}.
                            </p>
                          )}
                          {partnerInfo.can_use_credits && (
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Credits will be deducted on order
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                <hr className="border-[#e5e5e5]" />
                
                {/* Show discount if using partner credits */}
                {usePartnerCredits && partnerInfo?.is_partner && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#737373]">Subtotal (Base)</span>
                      <span className="font-mono text-[#737373] line-through">₹{calculateTotal().toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Partner Discount ({partnerInfo.discount_rate}%)</span>
                      <span className="font-mono">-₹{partnerInfo.discount_amount?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#737373]">Discounted Base</span>
                      <span className="font-mono text-[#1a1a1a]">₹{getBaseDiscountedPrice().toLocaleString('en-IN')}</span>
                    </div>
                  </>
                )}
                
                {/* GST Breakdown */}
                {!usePartnerCredits && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">Subtotal (Base)</span>
                    <span className="font-mono text-[#1a1a1a]">₹{calculateTotal().toLocaleString('en-IN')}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-[#737373]">GST ({gstRate}%)</span>
                  <span className="font-mono text-[#1a1a1a]">
                    ₹{(usePartnerCredits && partnerInfo?.is_partner 
                      ? Math.round(getBaseDiscountedPrice() * (gstRate / 100))
                      : calculateGST()
                    ).toLocaleString('en-IN')}
                  </span>
                </div>
                
                <hr className="border-[#e5e5e5]" />
                
                <div className="flex justify-between items-center">
                  <span className="font-playfair text-xl font-bold text-[#1a1a1a]">Total</span>
                  <span className="font-mono text-2xl font-bold text-[#D4AF37]">
                    ₹{getDiscountedTotal().toLocaleString('en-IN')}
                  </span>
                </div>
                
                <p className="font-manrope text-xs text-[#a3a3a3]">
                  {itemType === 'print_media' ? 'GST @ 5%' : 'GST @ 18%'} included in total
                </p>
              </div>
              
              {isLoggedIn ? (
                <button
                  onClick={handleCheckout}
                  disabled={processing || (usePartnerCredits && !partnerInfo?.can_use_credits)}
                  data-testid="proceed-to-payment-button"
                  className="w-full px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    'Processing...'
                  ) : usePartnerCredits && partnerInfo?.can_use_credits ? (
                    <>
                      <Wallet className="w-4 h-4" />
                      Pay with Credits
                    </>
                  ) : (
                    'Proceed to Payment'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  data-testid="login-to-pay-button"
                  className="w-full px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Login to Continue
                </button>
              )}
              
              <p className="font-manrope text-xs text-[#a3a3a3] mt-4 text-center">
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Checkout;